import React from 'react';
import {
  Typography,
  Button,
  Box,
  Card,
  CardContent,
  Chip,
} from '@mui/material';
import Theme from 'theme';
import { formatTimeDifference } from 'utils/dateUtils';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import RefreshIcon from '@mui/icons-material/Refresh';
import { UseQueryResult } from 'react-query';
import { HrRefreshHistory, HrRefreshHistoryStatus } from 'pages/Dashboard/types/healthRecords.types';
import { trackMixpanelEvent } from 'utils/utilMethods';
import { MixpanelEventName } from 'utils/constants';
import { useHttp } from 'hooks/use-fetch';

interface HealthRecordRefreshProps {
    isRecordStatusRetreived: boolean;
    isLoading: boolean;
    handleRecordRefreshDrawer: () => void;
    handleMedicationHistoryDrawer: () => void;
    getHrRefreshHistory: UseQueryResult<HrRefreshHistory, unknown>;
    refreshStatus: HrRefreshHistoryStatus | undefined;
    hrCount: number;
    hrCountFromHistory: number;
    selectedChip: boolean;
    setSelectedChip: (value: boolean) => void;
    handleChipClick: () => void;
    isDemoPatient?: boolean;
}

function HealthRecordRefresh({
  isRecordStatusRetreived,
  isLoading,
  handleRecordRefreshDrawer,
  getHrRefreshHistory,
  refreshStatus, hrCount,
  hrCountFromHistory,
  selectedChip,
  isDemoPatient,
  setSelectedChip,
  handleChipClick,
  handleMedicationHistoryDrawer,
}: HealthRecordRefreshProps) {
  const { isHrRefreshEnabled, providerId, providerName, selectedProviderName } = useHttp();
  let refreshContent = null;

  const [refreshing, setRefreshing] = React.useState(false);

  React.useEffect(() => {
    const interval = setInterval(() => {
      setRefreshing(true);
      setTimeout(() => setRefreshing(false), 2000);
    }, 5000);

    return () => clearInterval(interval);
  }, []);

  if (isDemoPatient) {
    refreshContent = null;
  }
  else if (refreshStatus === HrRefreshHistoryStatus.IN_PROGRESS) {
    refreshContent = (
      <>
        <RefreshIcon
          sx={{
            color: '#0000008A',
            marginLeft: 1,
            marginRight: 0.5,
            animation: refreshing ? 'spin 2s infinite linear' : 'none',
            '@keyframes spin': {
              '0%': {
                transform: 'rotate(0deg)',
              },
              '100%': {
                transform: 'rotate(360deg)',
              },
            },
          }}
        />
        <Typography
          fontSize={16}
          fontWeight={400}
          color={Theme.custom.colors.primaryMain}
          variant='body1'
          onClick={handleMedicationHistoryDrawer}
          sx={{
            cursor: 'pointer',
            '&:hover': {
              textDecoration: 'underline',
            },
          }}
        >
          Refresh in progress
        </Typography>
      </>
    );
  }
  else if (refreshStatus === HrRefreshHistoryStatus.COMPLETED) {
    refreshContent = (
      <Typography
        fontSize={16}
        fontWeight={400}
        color={Theme.custom.colors.primaryMain}
        variant='body1'
        onClick={handleMedicationHistoryDrawer}
        sx={{
          cursor: 'pointer',
          '&:hover': {
            textDecoration: 'underline',
          },
        }}
      >
        Refreshed
        {' '}
        {getHrRefreshHistory.data?.refreshHistory[0]?.scheduledOn
          ? formatTimeDifference(getHrRefreshHistory.data?.refreshHistory[0]?.scheduledOn)
          : null}
      </Typography>
    );
  }
  else if (refreshStatus === HrRefreshHistoryStatus.INTIAL_REQUEST) {
    refreshContent = (
      <Typography
        fontSize={16}
        fontWeight={400}
        color={Theme.custom.colors.primaryMain}
        variant='body1'
        onClick={handleMedicationHistoryDrawer}
        sx={{
          cursor: 'pointer',
          '&:hover': {
            textDecoration: 'underline',
          },
        }}
      >
        Records retrieved
        {' '}
        {getHrRefreshHistory.data?.refreshHistory[0]?.scheduledOn
          ? formatTimeDifference(getHrRefreshHistory.data?.refreshHistory[0]?.scheduledOn)
          : null}
      </Typography>
    );
  }

  const deSelectChip = () => {
    setSelectedChip(false);
    trackMixpanelEvent(MixpanelEventName
      .PATIENT_DETAILS_SHOW_RECORDS_FROM_LAST_REFRESH_BUTTON_DESELECTED, {
      'fetch in progress yes/no': refreshStatus === HrRefreshHistoryStatus.IN_PROGRESS ? 'yes' : 'no',
      'provider id': providerId ?? '',
      'logged in provider name': providerName ?? '',
      'selected provider name': selectedProviderName ?? '',
      location: 'patient_history_tab_show_records_from_last_refresh',
    });
  };

  const showChipText = (hrCount > 0 || hrCountFromHistory > 0) && (getHrRefreshHistory
    .data?.refreshHistory?.length ?? 0) > 1;

  /*
  Determine whether the HR (Health Record) refresh feature should be enabled.

  It will be enabled if:
    - The record status *has* been successfully retrieved (!isRecordStatusRetreived === false), OR
    - There is more than one previous HR refresh history entry.

  AND:
    - The global HR refresh feature flag (isHrRefreshEnabled) is enabled.

  If none of these conditions are met, the value will be explicitly set to `undefined`.
*/
  const shouldEnableHrRefresh = (
    (
      !isRecordStatusRetreived // Means: record *has* been retrieved
      || (getHrRefreshHistory?.data?.refreshHistory?.length ?? 0) > 1
    )
    && isHrRefreshEnabled
  ) || undefined;

  const hrRecordLabel = (hrCount > 1 || hrCountFromHistory > 1) ? 'records' : 'record';

  const isDisabled = (getHrRefreshHistory.data?.refreshHistory[0]?.status
    === HrRefreshHistoryStatus.IN_PROGRESS) || isDemoPatient;

  // Show the "In Progress HRR" UI in the case when:
  // 1. The record status has *not* been retrieved yet (i.e., still in progress or pending),
  // 2. The system is *not* currently loading (isLoading is false), and
  // 3. There is a refresh history array available, and it has at most one entry.
  const showInProgressHRREnabled = isRecordStatusRetreived && !isLoading
  && (getHrRefreshHistory.data?.refreshHistory
    && getHrRefreshHistory.data.refreshHistory.length <= 1);

  // Show the "In Progress HRR" UI as *disabled* (e.g., maybe grayed out) when:
  // 1. The record status has *not* been retrieved,
  // 2. The system is *not* loading, and
  // 3. Either:
  //    a. There is a refresh history with at most one entry, OR
  //    b. The refresh history data is entirely undefined (i.e., not fetched yet)
  const showInProgressHRRDisabled = isRecordStatusRetreived && !isLoading
  && ((getHrRefreshHistory.data?.refreshHistory
    && getHrRefreshHistory.data.refreshHistory.length <= 1)
     || getHrRefreshHistory.data === undefined);

  return (
    <>
      {(showInProgressHRREnabled || showInProgressHRRDisabled) && (
        <Card sx={{ backgroundColor: '#E9F9EA',
          borderRadius: 3,
          marginBottom: 3,
          border: '1px solid',
          boxShadow: 'none',
          width: '50%',
          borderColor: Theme.custom
            .colors.lightSuccessMain }}
        >
          <CardContent>
            <Box display='flex' alignItems='center' gap={1}>
              <AutorenewIcon
                color='success'
                sx={{
                  animation: refreshing ? 'spin 2s infinite linear' : 'none',
                  '@keyframes spin': {
                    '0%': {
                      transform: 'rotate(0deg)',
                    },
                    '100%': {
                      transform: 'rotate(360deg)',
                    },
                  },
                }}
              />
              <Typography variant='h6'>
                In progress
              </Typography>
            </Box>
            <Typography variant='body1' marginTop={1.5}>
              Records are still being retrieved. Any information shown may not be complete.
            </Typography>
          </CardContent>
        </Card>
      )}
      {shouldEnableHrRefresh && (
      <Box
        display='flex'
        justifyContent='flex-start'
        alignItems='center'
        marginBottom={3}
      >
        <Button
          onClick={() => handleRecordRefreshDrawer()}
          variant='contained'
          size='large'
          disabled={isDisabled}
          sx={{
            marginRight: 2,
            backgroundColor: !isDisabled ? Theme.custom.colors.primaryMain : undefined,
            color: !isDisabled ? Theme.custom.colors.white : undefined,
            boxShadow: `
      0px 1px 5px 0px #0000001F,
      0px 2px 2px 0px #00000024,
      0px 3px 1px -2px #00000033
    `,
            '&:hover': {
              backgroundColor: !isDisabled ? Theme.custom.colors.primaryDark : undefined,
            },
          }}
          startIcon={
            <RefreshIcon fontSize='large' />
        }
        >
          REFRESH RECORDS
        </Button>
        <Box display='flex' alignItems='center'>
          {refreshContent}
          {getHrRefreshHistory.data?.refreshHistory[0]?.hrCount === 0
                && getHrRefreshHistory.data?.refreshHistory[0]?.status
                === HrRefreshHistoryStatus.COMPLETED && (
                <Typography
                  fontSize={16}
                  fontWeight={400}
                  marginLeft={1}
                  color={Theme.custom.colors.lightTextPrimary}
                  variant='body1'
                >
                  - 0 new records found
                </Typography>
          )}
          {showChipText && (
            <Chip
              label={`Show ${(hrCount || hrCountFromHistory) === 1 ? 'record' : 'records'}
               from latest refresh (${hrCount || hrCountFromHistory})`}
              onClick={handleChipClick}
              onDelete={selectedChip ? deSelectChip : undefined}
              color='primary'
              clickable
              sx={{
                backgroundColor: selectedChip ? '#22516B' : '#E0E0E0',
                color: selectedChip ? '#FFFFFF' : '#000000',
                marginLeft: 2,
                '&:hover': {
                  color: '#FFFFFF',
                },
              }}
            />
          )}
        </Box>
      </Box>
      )}
      {showChipText && selectedChip && isHrRefreshEnabled && (
        <Typography variant='h6' fontWeight={500} fontSize={20} color={Theme.custom.colors.lightTextSecondary} mb={3}>
          Showing results
          {' '}
          <span style={{ color: Theme.custom.colors.lightTextPrimary }}>
            (
            {hrCount || hrCountFromHistory}
            {' '}
            {hrRecordLabel}
            )
          </span>
          {' '}
          from latest data refresh
        </Typography>
      )}
    </>
  );
}

export default HealthRecordRefresh;
